import { Link, useLocation } from 'react-router-dom';
import { NAV_PAGES, type NavPage } from '../app/navigation';
import { Chip } from '../components/Chip';
import { Mono } from '../components/Mono';
import { Panel } from '../components/Panel';
import { AccessGate, PendingApiPanel } from '../components/States';
import { useCapability } from '../lib/capability';
import { capabilityLevelLabel } from '../lib/labels';

function resolve(pathname: string): NavPage | undefined {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return NAV_PAGES.find((page) => `/${page.path}` === clean);
}

/**
 * Route shell for the pages this task deliberately does not build.
 *
 * It renders no numbers and no fake table rows: it states the plan page, the capability that will
 * open it, what the page will do, which endpoints it waits for (with the coverage marker from
 * `docs/API_COVERAGE_FA.md`) and why it is still a shell — API pending vs UI pending are different
 * facts and are shown separately.
 *
 * `pending` is not a licence to skip the access state: RBAC §۹ refuses «حذف بی‌صدای صفحه» for a
 * pending shell too, so the shell sits behind the same `<AccessGate>` every live page uses and a
 * caller with no level for the page's key sees `DeniedState`.
 */
export function PendingPage() {
  const location = useLocation();
  const page = resolve(location.pathname);

  if (!page) {
    return (
      <Panel title="صفحه‌ای پیدا نشد" icon="warning">
        <p className="text-body text-ink-dim">
          مسیر <Mono value={location.pathname} className="mx-1" /> در رجیستری کنسول ثبت نشده است. از
          ناوبری سمت راست یک صفحه انتخاب کنید.
        </p>
        <Link to="/" className="btn btn-ghost btn-sm mt-3">
          رفتن به داشبورد
        </Link>
      </Panel>
    );
  }

  const reason = page.pendingReason === 'api' ? 'منتظر API' : 'منتظر UI';
  return (
    <AccessGate
      capability={page.capability}
      hint={`این صفحه با کلید ${page.capability} باز می‌شود؛ سطح شما از ماتریس همین استقرار خوانده می‌شود.`}
    >
      <PendingShell page={page} reason={reason} />
    </AccessGate>
  );
}

function PendingShell({ page, reason }: { page: NavPage; reason: string }) {
  const level = useCapability(page.capability);
  return (
    <div className="space-y-block" data-testid={`page-${page.id}`}>
      <Panel
        title={`${page.title} — صفحهٔ ${page.planPage} طرح §3`}
        icon={page.icon}
        subtitle={page.pendingNote}
        actions={
          <div className="flex items-center gap-2">
            <Chip tone="warning" label={reason} dot={false} />
            <Chip
              tone="neutral"
              label={`سطح: ${capabilityLevelLabel(level)}`}
              title={page.capability}
              dot={false}
            />
          </div>
        }
      >
        <p className="text-body text-ink-dim">
          این یک route shell است: ناوبری و مسیر ساختار یافته‌اند اما داده‌ای نمایش داده نمی‌شود، چون
          صفحه‌های {page.planPage} در task های بعدی همین batch (W2/W3 و فاز E) ساخته می‌شوند.
        </p>
        <ul className="mt-3 space-y-1 text-body text-ink-dim">
          <li>· ورودی از ناوبری کنسول و breadcrumb ثبت شده است</li>
          <li>
            · عنوان، کلید <Mono value={page.capability} className="mx-1" />و کارکرد از جدول صفحات
            طرح §3 و رجیستری capability خوانده می‌شود
          </li>
          <li>· هیچ شمارنده یا جدول نمونه‌ای در این صفحه ساخته نمی‌شود</li>
        </ul>
      </Panel>

      <PendingApiPanel
        title="وابستگی‌های این صفحه"
        refs={page.apiRefs}
        note={
          page.pendingReason === 'api'
            ? 'به‌محض رسیدن endpoint ها، همین route به صفحهٔ داده‌محور تبدیل می‌شود (بدون تغییر لایهٔ API).'
            : 'endpoint ها موجودند؛ صفحه در task بعدی (W3) ساخته می‌شود.'
        }
      />

      <div className="flex gap-2">
        <Link to="/devices" className="btn btn-ghost btn-sm">
          موجودی دستگاه‌ها
        </Link>
        <Link to="/" className="btn btn-ghost btn-sm">
          داشبورد
        </Link>
      </div>
    </div>
  );
}
